import { useState, useEffect } from 'react';
import api from '../api/client';
import TagBadge from './TagBadge';

interface SystemTag {
  _id: string;
  name: string;
  color: string;
  description?: string;
}

const PRESET_COLORS = [
  '#EF4444', // red
  '#F97316', // orange
  '#F59E0B', // amber
  '#22C55E', // green
  '#14B8A6', // teal
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#6B7280', // gray
];

export default function TagManagement() {
  const [tags, setTags] = useState<SystemTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [editingTag, setEditingTag] = useState<SystemTag | null>(null);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(PRESET_COLORS[0]);
  const [newTagDescription, setNewTagDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/tags');
      setTags(response.data.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    try {
      setSaving(true);
      const response = await api.post('/admin/tags', {
        name: newTagName.trim(),
        color: newTagColor,
        description: newTagDescription.trim() || undefined,
      });
      setTags([...tags, response.data.data]);
      setNewTagName('');
      setNewTagDescription('');
      setNewTagColor(PRESET_COLORS[0]);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create tag');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag || !newTagName.trim()) return;

    try {
      setSaving(true);
      const response = await api.put(`/admin/tags/${editingTag._id}`, {
        name: newTagName.trim(),
        color: newTagColor,
        description: newTagDescription.trim() || undefined,
      });
      setTags(tags.map(t => t._id === editingTag._id ? response.data.data : t));
      setEditingTag(null);
      setNewTagName('');
      setNewTagDescription('');
      setNewTagColor(PRESET_COLORS[0]);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update tag');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    if (!confirm('Are you sure you want to delete this tag? It will be removed from all claimants.')) {
      return;
    }

    try {
      await api.delete(`/admin/tags/${tagId}`);
      setTags(tags.filter(t => t._id !== tagId));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete tag');
    }
  };

  const startEditing = (tag: SystemTag) => {
    setEditingTag(tag);
    setNewTagName(tag.name);
    setNewTagColor(tag.color);
    setNewTagDescription(tag.description || '');
  };

  const cancelEditing = () => {
    setEditingTag(null);
    setNewTagName('');
    setNewTagColor(PRESET_COLORS[0]);
    setNewTagDescription('');
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Tag Management</h2>
        <div className="animate-pulse">Loading tags...</div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Tag Management</h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Create/Edit Form */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-md">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {editingTag ? 'Edit Tag' : 'Create New Tag'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              placeholder="e.g., VIP, Priority, Suspicious"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              maxLength={50}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Color
            </label>
            <div className="flex gap-1 flex-wrap">
              {PRESET_COLORS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setNewTagColor(color)}
                  className={`w-7 h-7 rounded-full border-2 ${
                    newTagColor === color
                      ? 'border-gray-900 dark:border-white'
                      : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (optional)
            </label>
            <input
              type="text"
              value={newTagDescription}
              onChange={(e) => setNewTagDescription(e.target.value)}
              placeholder="Brief description of when to use this tag"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          {newTagName && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">Preview:</span>
              <TagBadge name={newTagName} color={newTagColor} size="md" />
            </div>
          )}
          <div className="ml-auto flex gap-2">
            {editingTag && (
              <button
                type="button"
                onClick={cancelEditing}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={editingTag ? handleUpdateTag : handleCreateTag}
              disabled={saving || !newTagName.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : editingTag ? 'Update Tag' : 'Create Tag'}
            </button>
          </div>
        </div>
      </div>

      {/* Tags List */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Existing Tags</h3>
        {tags.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No tags created yet.</p>
        ) : (
          <div className="space-y-2">
            {tags.map(tag => (
              <div
                key={tag._id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md"
              >
                <div className="flex items-center gap-3">
                  <TagBadge name={tag.name} color={tag.color} size="md" />
                  {tag.description && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {tag.description}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => startEditing(tag)}
                    className="p-1 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                    title="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteTag(tag._id)}
                    className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
